import Vue from 'vue';
import Router from 'vue-router';
import store from '@core/store/store';
import observability from '@shared_components/admin/pages/api-observability.cjs';
import {
    requiresNotAuthGuard,
    requiresAuthGurard
} from './guard';
import {
    scrollBehavior,
    setupNavigationGuards
} from '@shared_utils/router';

const routes = [
    {
        path: '/maintenance',
        name: 'Maintenance',
        component: () => import('@shared_components/common/MaintenancePage'),
        meta: {requireAuth: false, dashboardType: 'common'}
    },
    {
        path: '/',
        name: 'Login',
        component: () => import('@/components/auth/Login'),
        meta: {requireAuth: false, dashboardType: 'common'},
        beforeEnter: async (to, from, next) => {
            await requiresNotAuthGuard(to, from, next);
        }
    },
    {
        path: '/reg',
        name: 'Register',
        component: () => import('@/components/auth/Register/Register.vue'),
        props: true,
        meta: {requireAuth: false, dashboardType: 'common'},
    },
    {
        // 새 벤더코드 참여 요청 (#442) — /reg 에서 링크하지 않는다. 필요 시 주소를 직접 안내.
        path: '/reg/join',
        name: 'RegisterJoinRequest',
        component: () => import('@/components/auth/Register/RegisterJoinRequest.vue'),
        meta: {requireAuth: false, dashboardType: 'common'},
    },
    {
        path: '/reg/auth',
        name: 'RegisterAuthorization',
        component: () => import('@shared_components/common/RegAuth'),
        props: true,
        meta: {requireAuth: false, dashboardType: 'common'},
        // beforeEnter: async (to, from, next) => { await requiresNotAuthGuard (to, from, next) }
    },
    {
        path: '/findPassword',
        name: 'findPassword',
        component: () => import('@/components/auth/FindPassword'),
        meta: {requireAuth: false, dashboardType: 'common'},
    },
    {
        path: '/mcp/authorize',
        name: 'McpAuthorize',
        component: () => import('@shared_components/common/McpAuthorize'),
        meta: {requireAuth: true, dashboardType: 'participant'},
        beforeEnter: async (to, from, next) => {
            await requiresAuthGurard(to, from, next);
        }
    },
    {
        path: '',
        component: () => import('@shared_components/client/layout/MainMaster'),
        children: [
            {
                path: '/main',
                name: 'Main',
                components: {
                    header: () => import('@shared_components/common/Header'),
                    default: () => import('@shared_components/client/pages/IntroLanding'),
                    footer: () => import('@shared_components/common/Footer')
                },
                meta: {requireAuth: true, dashboardType: 'participant'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            }
        ],
        props: true,
        meta: {requireAuth: true, dashboardType: 'participant'},
        beforeEnter: async (to, from, next) => {
            await requiresAuthGurard(to, from, next);
        }
    },
    {
        path: '/pdf_viewer',
        name: 'PdfViewer',
        component: () => import('@shared_components/common/PdfStandardViewer'),
        props: true
    },
    {
        path: '',
        component: () => import('@shared_components/client/layout/ParticipateMaster'),
        children: [
            {
                path: '/participate_preface',
                name: 'ParticipatePreface',
                components: {
                    header: () => import('@shared_components/common/Header'),
                    aside: () => import('@shared_components/client/components/QuestionList'),
                    default: () => import('@shared_components/client/pages/ParticipateLanding'),
                    footer: () => import('@shared_components/common/Footer')
                },
                props: true,
                meta: {requireAuth: true, dashboardType: 'participant'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: '/questions/:question_code',
                name: 'Questions',
                components: {
                    header: () => import('@shared_components/common/Header'),
                    aside: () => import('@shared_components/client/components/QuestionList'),
                    default: () => import('@shared_components/client/pages/Questions'),
                    footer: () => import('@shared_components/common/Footer')
                },
                props: true,
                meta: {requireAuth: true, dashboardType: 'participant'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            }
        ],
        props: true,
        meta: {requireAuth: true, dashboardType: 'participant'}
    },
    {
        path: '/terms_layout',
        name: 'TermsLayout',
        components: {default: () => import('@shared_components/utils/service_policies/TermsLayout')},
        meta: {requireAuth: false, dashboardType: 'common'}
    },
    {
        path: '',
        component: () => import('@shared_components/admin/layout/AdminMaster'),
        children: [
            {
                path: '/api_observability',
                redirect: {name: 'ApiObservability'},
            },
            {
                path: '/project_code_list',
                name: 'ProjectCodeList',
                components: {
                    header: () => import('@shared_components/admin/components/common/AdminHeader'),
                    default: () => import('@shared_components/admin/pages/ProjectCodeList'),
                    footer: () => import('@shared_components/common/Footer')
                },
                meta: {requireAuth: true, dashboardType: 'participant'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                },
            },
            {
                path: '/report_bulk',
                name: 'ReportBulkRun',
                components: {
                    header: () => import('@shared_components/admin/components/common/AdminHeader'),
                    default: () => import('@shared_components/admin/pages/ReportBulkRun'),
                    footer: () => import('@shared_components/common/Footer')
                },
                meta: {requireAuth: true, dashboardType: 'participant'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                },
            },
            {
                path: '/campaign_list',
                name: 'CampaignList',
                components: {
                    header: () => import('@shared_components/admin/components/common/AdminHeader'),
                    default: () => import('@shared_components/admin/pages/CampaignList'),
                    footer: () => import('@shared_components/common/Footer')
                },
                meta: {requireAuth: true, dashboardType: 'participant'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                },
            },
            {
                path: '/editing_campaign_attr',
                name: 'EditingCampaignAttr',
                components: {
                    header: () => import('@shared_components/admin/components/common/AdminHeader'),
                    default: () => import('@shared_components/admin/pages/EditingCampaignAttr'),
                    footer: () => import('@shared_components/common/Footer')
                },
                meta: {requireAuth: true, dashboardType: 'participant'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: '/template_list',
                name: 'TemplateList',
                components: {
                    header: () => import('@shared_components/admin/components/common/AdminHeader'),
                    default: () => import('@shared_components/admin/pages/TemplateList'),
                    footer: () => import('@shared_components/common/Footer')
                },
                meta: {requireAuth: true, dashboardType: 'participant'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: '/managing_criteria',
                name: 'ManagingCriteria',
                components: {
                    header: () => import('@shared_components/admin/components/common/AdminHeader'),
                    default: () => import('@shared_components/admin/pages/managing_criteria.vue'),
                    footer: () => import('@shared_components/common/Footer')
                },
                meta: {requireAuth: true, dashboardType: 'participant'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: '/managing_parser_values',
                name: 'ManagingParserValues',
                components: {
                    header: () => import('@shared_components/admin/components/common/AdminHeader'),
                    default: () => import('@shared_components/admin/pages/ManagingParserValues.vue'),
                    footer: () => import('@shared_components/common/Footer')
                },
                meta: {requireAuth: true, dashboardType: 'participant'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: '',
                components: {
                    header: () => import('@shared_components/admin/components/common/AdminHeader'),
                    default: () => import('@shared_components/admin/layout/AdminSubLayout'),
                    footer: () => import('@shared_components/common/Footer')
                },
                children: [
                    {
                        path: '/campaign_report',
                        name: 'CampaignReport',
                        components: {
                            header: () => import('@shared_components/admin/components/common/AdminHeader'),
                            default: () => import('@/pages/admin/CampaignReport_v2'),
                            footer: () => import('@shared_components/common/Footer')
                        },
                        meta: {requireAuth: true, dashboardType: 'participant'},
                        beforeEnter: async (to, from, next) => {
                            await requiresAuthGurard(to, from, next);
                        }
                    },
                    {
                        path: '/answers',
                        name: 'Answers',
                        components: {
                            header: () => import('@shared_components/admin/components/common/AdminHeader'),
                            default: () => import('@shared_components/admin/pages/Answers.vue'),
                            footer: () => import('@shared_components/common/Footer')
                        },
                        meta: {requireAuth: true, dashboardType: 'participant'},
                        beforeEnter: async (to, from, next) => {
                            await requiresAuthGurard(to, from, next);
                        }
                    },
                    {
                        path: '/answers/:company_id',
                        name: 'CompanyAnswers',
                        components: {
                            header: () => import('@shared_components/admin/components/common/AdminHeader'),
                            default: () => import('@/pages/admin/CompanyAnswer_v2.vue'),
                            footer: () => import('@shared_components/common/Footer')
                        },
                        meta: {requireAuth: true, dashboardType: 'participant'},
                        beforeEnter: async (to, from, next) => {
                            await requiresAuthGurard(to, from, next);
                        }
                    },
                    {
                        path: '/project_progress',
                        name: 'ProjectProgress',
                        components: {
                            header: () => import('@shared_components/admin/components/common/AdminHeader'),
                            default: () => import('@shared_components/admin/pages/ProjectProgress.vue'),
                            footer: () => import('@shared_components/common/Footer')
                        },
                        meta: {requireAuth: true, dashboardType: 'participant'},
                        beforeEnter: async (to, from, next) => {
                            await requiresAuthGurard(to, from, next);
                        },
                    },
                    {
                        path: '/campaign_progress',
                        name: 'CampaignProgress',
                        components: {
                            header: () => import('@shared_components/admin/components/common/AdminHeader'),
                            default: () => import('@shared_components/admin/pages/CampaignProgress.vue'),
                            footer: () => import('@shared_components/common/Footer')
                        },
                        meta: {requireAuth: true, dashboardType: 'participant'},
                        beforeEnter: async (to, from, next) => {
                            await requiresAuthGurard(to, from, next);
                        },
                    }
                ],
                props: true,
            },
            {
                path: '/editing_template',
                name: 'EditingTemplate',
                components: {
                    header: () => import('@shared_components/admin/components/common/AdminHeader'),
                    default: () => import('@/pages/admin/EditingTemplate_v2.vue'),
                    footer: () => import('@shared_components/common/Footer')
                },
                props: true,
                meta: {requireAuth: true, dashboardType: 'participant'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                // Draft — 문항 구조 v2 편집기. 기존 /editing_template 은 그대로 두고 별도 경로로 띄운다.
                path: '/structure_v2_draft',
                name: 'StructureEditorDraft',
                components: {
                    header: () => import('@shared_components/admin/components/common/AdminHeader'),
                    default: () => import('@/pages/admin/StructureEditorDraft.vue'),
                    footer: () => import('@shared_components/common/Footer')
                },
                props: true,
                meta: {requireAuth: true, dashboardType: 'participant'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: '/editing_template_translation/:template_code',
                name: 'EditingTemplateTranslation',
                components: {
                    header: () => import('@shared_components/admin/components/common/AdminHeader'),
                    default: () => import('@shared_components/admin/pages/EditingTemplateTranslation.vue'),
                    footer: () => import('@shared_components/common/Footer')
                },
                props: true,
                meta: {requireAuth: true, dashboardType: 'participant'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
        ],
        props: true,
        meta: {requireAuth: true, dashboardType: 'participant'}
    },
    {
        path: '/admin',
        component: () => import('@shared_components/admin/layout/layout-admin.vue'),
        meta: {requireAuth: true, dashboardType: 'admin'},
        beforeEnter: async (to, from, next) => {
            await requiresAuthGurard(to, from, next);
        },
        children: [
            {
                path: 'api_observability',
                name: 'ApiObservability',
                component: () => import('@shared_components/admin/pages/Observability.vue'),
                meta: {requireAuth: true, requiresAuth: true, dashboardType: 'admin', title_kr: 'API 이용 현황'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, destination => {
                        if (destination) next(destination);
                        else if (observability.canObserve(store.getters.isAdmin)) next();
                        else next({name: 'Main'});
                    });
                },
            },
            {
                path: 'dashboard',
                name: 'MainDashboard',
                component: () => import('@shared_components/admin/pages/dashboard.vue'),
                meta: {requireAuth: true, dashboardType: 'admin', title_kr: '대시보드'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: 'assessment',
                name: 'ManagingAssessment',
                component: () => import('@shared_components/admin/pages/AdminAssessment.vue'),
                meta: {requireAuth: true, dashboardType: 'admin', title_kr: '평가 관리'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: 'projectCode',
                name: 'ManagingProjectCode',
                component: () => import('@shared_components/admin/pages/AdminProjectCode.vue'),
                meta: {requireAuth: true, dashboardType: 'admin', title_kr: '프로젝트코드 관리'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: 'projectGroup',
                name: 'ManagingProjectGroup',
                component: () => import('@shared_components/admin/pages/Management/ProjectGroup/ProjectGroupManagement.vue'),
                meta: {requireAuth: true, dashboardType: 'admin', title_kr: '평가 그룹 관리'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: 'assignment_console',
                name: 'ManagingProjectConsole',
                component: () => import('@shared_components/admin/pages/Management/ProjectConsole/ProjectConsole.vue'),
                meta: {requireAuth: true, dashboardType: 'admin', title_kr: '프로젝트 배정 콘솔'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: 'campaign_connect_rule',
                name: 'ManagingCampaignConnectRule',
                component: () => import('@shared_components/admin/pages/Management/CampaignConnectRule/CampaignConnectRule.vue'),
                meta: {requireAuth: true, dashboardType: 'admin', title_kr: '캠페인 연결 규칙 관리 (고급)'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: 'project_schema',
                name: 'ManagingProjectSchema',
                component: () => import('@shared_components/admin/pages/Management/ProjectSchema/ProjectSchema.vue'),
                meta: {requireAuth: true, dashboardType: 'admin', title_kr: '프로젝트 스키마 관리'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: 'vendor_code',
                name: 'ManagingVendorCode',
                component: () => import('@shared_components/admin/pages/Management/VendorCode/VendorCodeManagement.vue'),
                meta: {requireAuth: true, dashboardType: 'admin', title_kr: '벤더코드 관리'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: 'vendor_join_request',
                name: 'ManagingVendorJoinRequest',
                component: () => import('@shared_components/admin/pages/Management/VendorJoinRequest/VendorJoinRequestManagement.vue'),
                meta: {requireAuth: true, dashboardType: 'admin', title_kr: '벤더코드 참여 요청'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: 'user',
                name: 'ManagingUser',
                component: () => import('@shared_components/admin/pages/AdminUser.vue'),
                meta: {requireAuth: true, dashboardType: 'admin', title_kr: '사용자 관리'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: 'company',
                name: 'ManagingParticipationCompany',
                component: () => import('@shared_components/admin/pages/Management/Company/CompanyManagement.vue'),
                meta: {requireAuth: true, dashboardType: 'admin', title_kr: '참여기업 관리'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: 'company/:company_id',
                name: 'CompanyDetail',
                component: () => import('@shared_components/admin/pages/Management/Company/CompanyManagementDetail.vue'),
                meta: {requireAuth: true, dashboardType: 'admin', title_kr: '기업 상세'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: 'client',
                name: 'ManagingClient',
                component: () => import('@shared_components/admin/pages/AdminClient.vue'),
                meta: {requireAuth: true, dashboardType: 'admin', title_kr: '원청사 관리'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: 'ai-assessment',
                name: 'ManagingAiAssessment',
                component: () => import('@/pages/admin/AIAssessment_v2.vue'),
                meta: {requireAuth: true, dashboardType: 'admin', title_kr: 'AI 평가 관리'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: 'eval-profiles',
                name: 'ManagingEvalProfiles',
                component: () => import('@shared_components/admin/pages/Management/EvalProfile/EvalProfileManagement.vue'),
                meta: {requireAuth: true, dashboardType: 'admin', title_kr: 'AI 평가 프로파일'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: 'supplement-review-list',
                name: 'ManagingSupplementReviewList',
                component: () => import('@/pages/admin/SupplementReviewList.vue'),
                meta: {requireAuth: true, dashboardType: 'admin', title_kr: 'AI 보완 검토'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: 'supplement-review',
                name: 'ManagingSupplementReview',
                component: () => import('@/pages/admin/SupplementReview.vue'),
                meta: {requireAuth: true, dashboardType: 'admin', title_kr: 'AI 보완검토'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: 'supplement-prompt',
                name: 'ManagingSupplementPrompt',
                component: () => import('@shared_components/admin/pages/Management/SupplementPrompt/SupplementPromptManagement.vue'),
                meta: {requireAuth: true, dashboardType: 'admin', title_kr: 'AI 프롬프트 관리'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: 'eval-prompt',
                name: 'ManagingEvalPrompt',
                component: () => import('@shared_components/admin/pages/Management/EvalPrompt/EvalPromptManagement.vue'),
                meta: {requireAuth: true, dashboardType: 'admin', title_kr: 'AI 판단 기준'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: 'download-center',
                name: 'DownloadCenter',
                component: () => import('@shared_components/admin/pages/Management/DownloadCenter/ManageDownloadCenter.vue'),
                meta: {requireAuth: true, dashboardType: 'admin', title_kr: '다운로드 센터'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: 'copy-attachments',
                name: 'CopyAttachments',
                component: () => import('@shared_components/admin/pages/Management/CopyAttachments/CopyAttachments.vue'),
                meta: {requireAuth: true, dashboardType: 'admin', title_kr: '이전 캠페인 첨부파일 복사'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: 'document-facts',
                name: 'DocumentFacts',
                component: () => import('@shared_components/admin/pages/Management/DocumentFacts/DocumentFacts.vue'),
                meta: {requireAuth: true, dashboardType: 'admin', title_kr: '문서 사실 추출'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: 'document-facts/batches',
                name: 'DocumentFactBatches',
                component: () => import('@shared_components/admin/pages/Management/DocumentFacts/DocumentFactBatches.vue'),
                meta: {requireAuth: true, dashboardType: 'admin', title_kr: '문서 사실 추출 배치 관리'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: 'campaign-remap',
                name: 'CampaignRemap',
                component: () => import('@shared_components/admin/pages/Management/CampaignRemap/CampaignRemap.vue'),
                meta: {requireAuth: true, dashboardType: 'admin', title_kr: '답변 보존 캠페인 재배정'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            // 메일 관리
            { path: 'mail', name: 'MailDashboard', component: () => import('@shared_components/admin/pages/Management/Mail/MailDashboard.vue'), meta: {requireAuth: true, dashboardType: 'admin', title_kr: '이메일 템플릿 관리'} },
            { path: 'mail/editor', name: 'MailEditorNew', component: () => import('@shared_components/admin/pages/Management/Mail/MailEditor/MailEditor.vue'), meta: {requireAuth: true, dashboardType: 'admin', title_kr: '이메일 템플릿 편집'} },
            { path: 'mail/editor/:id', name: 'MailEditorEdit', component: () => import('@shared_components/admin/pages/Management/Mail/MailEditor/MailEditor.vue'), meta: {requireAuth: true, dashboardType: 'admin', title_kr: '이메일 템플릿 편집'} },
            { path: 'mail/snippet', name: 'MailSnippet', component: () => import('@shared_components/admin/pages/Management/Mail/MailSnippet/MailSnippet.vue'), meta: {requireAuth: true, dashboardType: 'admin', title_kr: '스니펫 관리'} },
            { path: 'mail/simulation', name: 'MailSimulation', component: () => import('@shared_components/admin/pages/Management/Mail/MailSimulation/MailSimulation.vue'), meta: {requireAuth: true, dashboardType: 'admin', title_kr: '템플릿 시뮬레이션'} },
            { path: 'mail/project-brands', name: 'ProjectBrandManagement', component: () => import('@shared_components/admin/pages/Management/Mail/ProjectBrand/ProjectBrandManagement.vue'), meta: {requireAuth: true, dashboardType: 'admin', title_kr: '메일 브랜드(프로젝트) 관리'} },
        ]
    },
    {
        path: '/requests/:requestsId',
        component: () => import('@shared_components/contractor/layout/layout-contractor.vue'),
        meta: {requireAuth: true, dashboardType: 'contractor'},
        beforeEnter: async (to, from, next) => {
            await requiresAuthGurard(to, from, next);
        },
        children: [
            {
                path: 'progress',
                name: 'ContractorProgress',
                component: () => import('@shared_components/contractor/pages/contractor-progress.vue'),
                meta: {requireAuth: true, dashboardType: 'contractor', title_kr: '진행 현황'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: 'report',
                name: 'ContractorReport',
                component: () => import('@shared_components/contractor/pages/contractor-report.vue'),
                meta: {requireAuth: true, dashboardType: 'contractor', title_kr: '평가 결과'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },
            {
                path: 'action-plan',
                name: 'ContractorActionPlan',
                component: () => import('@shared_components/contractor/pages/contractor-action-plan.vue'),
                meta: {requireAuth: true, dashboardType: 'contractor', title_kr: '개선계획 제출 현황'},
                beforeEnter: async (to, from, next) => {
                    await requiresAuthGurard(to, from, next);
                }
            },

        ]
    }, 
    {
        path: '/test/autocomplete',
        name: 'AutoCompleteTest',
        component: () => import('@shared_components/test/auto-complete-page.vue'),
    }
    
];

Vue.use(Router);



const router = new Router({
    mode: 'history',
    routes: routes.filter(e => {
        if(process.env.NODE_ENV === 'production') {
            return !e.path.includes('test'); // 운영일 때 테스트 페이지 제외
        }
        return true;
    }),
    scrollBehavior: (to, from) => scrollBehavior(to, from),
});

setupNavigationGuards(router);


export default router;
